import React from 'react';
import PropTypes from 'prop-types';
import Form from 'react-form-controlled';
import Validate from 'react-form-controlled-validate';
import MessageDefinitions from '../i18n/MessageDefinitions';
import { FormattedMessage, intlShape } from 'react-intl';
import { textInput } from '../../common/util/FormInput';
import FontAwesome from 'react-fontawesome';
import LocationsMap from './LocationsMap';
import {connectToStores} from "fluxible-addons-react";
import {changeAppointmentFlow, mapLocationSelected, saveZip} from "../actions/ContactMeActionCreators";
import Screens from '../constants/Screens';
import {LocationShape} from "../../common/react/Shapes";
import classnames from "classnames";

function pruneState(inState = {}) {
  const outState = {};
  Object.keys(inState || {}).forEach(key =>{
    if (inState[key] !== '') {
      outState[key] = inState[key];
    }
  });

  return outState;
}

@connectToStores(['ContactMeStore'], (context, props) => {
  const ContactMeStore = context.getStore('ContactMeStore');
  return {
    selectedLocation: ContactMeStore.getSelectedLocation(),
    form: ContactMeStore.getZipCode()
  };
})
class AppointmentLocationForm extends React.Component {
  static propTypes = {
    selectedLocation: LocationShape
  };

  static contextTypes = {
    executeAction: PropTypes.func.isRequired,
    intl: intlShape
  };

  static defaultSchema = {
    type: 'object',
    properties: {
      zipCode: {
        type: 'string',
        minLength: 5,
        'pattern' : '^[0-9]{5}(?:-[0-9]{4})?$'
      }
    },
    required: ['zipCode']
  };
  static defaultFields = ['zipCode'];

  constructor(props) {
    super(props);
    const { form = {} } = props;
    this.state = {
      errors: [],
      form: form,
      processing: false,
      locations: [],
      isLoading: false,
      showTheErrors: false,
      searchForLocationMatch: true,
      foundLocationMatch: false
    };
  }

  componentDidMount = () => {
    window.ignite.getAnswerService().then(answerService => {

      answerService.saveAnswer({
        appkey: 'makeAppointmentClicked',
        value: 'started',
        timestamp: new Date()
      });
      if(!this.state.form.zipCode) {
        answerService.getAnswer("postalCode").then(answer => {
          const zipCodeAnswer = answer.value ? answer.value[0] : null;
          if(zipCodeAnswer){
            const form = {
              zipCode: zipCodeAnswer
            };
            this.valueChanged(form);
          }
        });
      }
      this.validateForm(this.state.form || {});

    });
  };

  validateForm = state => {
    let errors = [];
    const prunedState = pruneState(state);
    this.form.validate(prunedState).then((formErrors = []) => {
      errors = formErrors.map(error => {
        return {
          key: error.path,
          errors: [error.message]
        };
      });

      this.setState({
        errors,
      });
    });
  };

  valueChanged = state => {
    const form = {
      ...state
    };
    this.validateForm(form);
    this.setState({
      form: form
    });
  };

  formSubmitted = state => {
    const form = {
      ...state
    };

    if(!this.props.selectedLocation.id){
      this.setState({
        showTheErrors: true
      });
    }else {
      this.setState({
        processing: true
      });
      const nextScreen = Screens.CALENDAR;
      this.context.executeAction(saveZip, {form});
      this.context.executeAction(changeAppointmentFlow, {nextScreen});
    }
  };

  updateLocations = ({lat, lng}) => {
    !this.state.isLoading ? this.setState({ isLoading: true }) : null;
    const searchForLocationMatch = this.state.searchForLocationMatch;
    window.ignite.appointmentService.getLocations({lat, lng, searchForLocationMatch}).then(locations => {
      const foundLocationMatch = searchForLocationMatch && locations.length === 1;
      this.setState({
        locations,
        isLoading: false,
        searchForLocationMatch: false,
        foundLocationMatch
      });
      if(foundLocationMatch){
        this.updateLocationSelection(locations[0]);
      }
    });
  };

  updateLocationSelection = location => {
    this.setState({
      showTheErrors: false
    });
    this.context.executeAction(mapLocationSelected, {location});
  };

  clearLocations = () => {
    this.setState({
      locations: []
    });
  };

  removeLocationMatchSettings = () => {
    this.setState({
      foundLocationMatch: false
    });
    this.updateLocationSelection(null);
  };

  render() {
    const formatMessage = this.context.intl.formatMessage;
    const { fields = AppointmentLocationForm.defaultFields, schema = AppointmentLocationForm.defaultSchema, selectedLocation } = this.props;
    const { errors = [], processing, form, locations, isLoading, showTheErrors, foundLocationMatch } = this.state;

    const formValid = !errors.length;
    const value = pruneState(this.state.form);

    const fieldErrors = {};
    errors.forEach(error => {
      fieldErrors[error.key] = true;
    });

    const formFields = fields.map(field => {
      let type = 'text';
      return textInput({
        type,
        name: field,
        labelMessage: MessageDefinitions[field],
        placeholderMessage: MessageDefinitions[`${field}Placeholder`],
        errorMessage: MessageDefinitions[`${field}Error`],
        hasError: fieldErrors[field],
        formatMessage
      });
    });

    const buttonMessage =
      processing
        ? <FontAwesome name="refresh" spin/>
        : <FormattedMessage {...MessageDefinitions.continue} />;

    const selectedLocationString =
      selectedLocation && selectedLocation.id
        ?
        <div className="selectedLocationString">Selected Location:
          <span className="name">{selectedLocation.name} </span>
          <span className="address">{selectedLocation.address}, </span>
          <span className="cityState">{selectedLocation.city}, {selectedLocation.state} {selectedLocation.postalCode}</span>
        </div>
        : null;

    const zipCode = formValid ? form.zipCode : null;

    const errorString =
      showTheErrors
      ? <div className="errorString"><FormattedMessage {...MessageDefinitions.appointmentLocationError} /></div>
      : null;

    const locationMatchedRender = null;

    const classes = classnames({
      'form-container': true,
      'loading-locations': isLoading,
      foundLocationMatch
    });

    const title =
    foundLocationMatch
      ? <FormattedMessage {...MessageDefinitions.appointmentLocationMatchMessage} />
      : <FormattedMessage {...MessageDefinitions.appointmentLocationFormMessage} />;

    const searchForMore =
      foundLocationMatch
        ? <div className="searchForMore" onClick={this.removeLocationMatchSettings}><FormattedMessage {...MessageDefinitions.appointmentLocationMatchSearchButton} /></div>
        : null;

    return (
      <Validate schema={schema} ref={f => this.form = f}>
        <Form value={value}
              className={classes}
              onChange={this.valueChanged}
              onSubmit={this.formSubmitted}>
          <div className="form-body location-section">
            <h5 className="appointment-header">{title}</h5>
            {selectedLocationString}
            {formFields}
            {searchForMore}
            <LocationsMap
              zipCode={zipCode}
              onClick={this.updateLocationSelection}
              selectedLocation={selectedLocation}
              onGeoLocationChange={this.updateLocations}
              onGeoLocationFailure={this.clearLocations}
              locations={locations}
              isLoading={isLoading}
            />
            {errorString}
          </div>
          <div className="form-actions">
            <button type="submit" className="btn btn-primary save" disabled={!formValid || processing || showTheErrors}>
              {buttonMessage}
            </button>
          </div>
        </Form>
      </Validate>
    );
  }
}

export default AppointmentLocationForm;



/** WEBPACK FOOTER **
 ** ./src/main/js/contactMe/components/AppointmentLocationForm.js
 **/